/* eslint-disable no-nested-ternary */
import { useState, useEffect, useRef } from 'react';
import { MdDelete, MdInsertDriveFile, MdEdit } from 'react-icons/md';
import { ImageEditorModal } from '../ImageEditor';

function isEditableImage(candidate: File): boolean {
  return (
    candidate.type.startsWith('image/') && candidate.type !== 'image/svg+xml'
  );
}

export function Dropzone({
  dropzoneId,
  label,
  optional,
  onDrop,
  fileTypesTagline,
  value,
  enableImageEditing,
  editorTitle,
  imageCropAspect,
  compact,
  maxFileSizeMb,
}: {
  dropzoneId?: string;
  label?: string;
  optional?: boolean;
  onDrop: (file: File | null) => void;
  fileTypesTagline?: string;
  value?: File | null;
  enableImageEditing?: boolean;
  editorTitle?: string;
  imageCropAspect?: number;
  compact?: boolean;
  maxFileSizeMb?: number;
}) {
  const [file, setFile] = useState<File | null>(value ?? null);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileToEdit, setFileToEdit] = useState<File | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync internal state with controlled value prop
  useEffect(() => {
    if (value) {
      setFile(value);
      if (value.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setPreview(reader.result as string);
        reader.readAsDataURL(value);
      } else {
        setPreview(null);
      }
    } else {
      setFile(null);
      setPreview(null);
    }
  }, [value]);

  const stageFile = (selectedFile: File | null) => {
    setFile(selectedFile);
    onDrop(selectedFile);
    if (selectedFile) {
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setPreview(reader.result as string);
        reader.readAsDataURL(selectedFile);
      } else {
        setPreview(null);
      }
    } else {
      setPreview(null);
    }
  };

  const clearFileInput = () => {
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleFile = (selectedFile: File | null) => {
    if (
      selectedFile &&
      maxFileSizeMb &&
      selectedFile.size > maxFileSizeMb * 1024 * 1024
    ) {
      setSizeError(
        `This file is too large. The maximum size is ${maxFileSizeMb}MB.`
      );
      return;
    }
    setSizeError(null);
    if (selectedFile && enableImageEditing && isEditableImage(selectedFile)) {
      setFileToEdit(selectedFile);
      return;
    }
    stageFile(selectedFile);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    clearFileInput();
    handleFile(selectedFile);
  };

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files?.[0] || null;
    handleFile(droppedFile);
  };

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
  };

  return (
    <div className='flex flex-col space-y-2 grow'>
      {label && (
        <div className='block text-sm leading-5 font-medium text-gray-900 font-inter'>
          {label}{' '}
          {optional && (
            <span className='text-sm leading-5 font-normal font-inter text-gray-400 ml-2'>
              Optional
            </span>
          )}
        </div>
      )}
      {file ? (
        preview ? (
          <div
            className={`flex flex-col items-center justify-center w-full ${
              compact ? 'h-36' : 'h-60'
            } rounded-lg bg-white relative`}
          >
            <img
              src={preview}
              alt={file.name}
              className={`object-contain ${
                compact ? 'max-h-36' : 'max-h-60'
              } max-w-full rounded`}
            />
            <div className='absolute top-2 right-2 flex gap-2'>
              {enableImageEditing && isEditableImage(file) && (
                <button
                  type='button'
                  onClick={() => setFileToEdit(file)}
                  aria-label='Edit image'
                  className='bg-primary hover:bg-blue-800 text-white rounded-full w-7 h-7 flex items-center justify-center shadow transition'
                >
                  <MdEdit size={18} />
                </button>
              )}
              <button
                type='button'
                onClick={() => {
                  setFile(null);
                  setPreview(null);
                  setSizeError(null);
                }}
                aria-label='Delete image'
                className='bg-red-600 hover:bg-red-700 text-white rounded-full w-7 h-7 flex items-center justify-center shadow transition'
              >
                <MdDelete size={20} />
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`flex items-center justify-center w-full ${
              compact ? 'h-36' : 'h-64'
            } border-2 border-gray-300 rounded-lg bg-white relative`}
          >
            <MdInsertDriveFile className='mr-2 text-3xl text-gray-500' />
            <span className='truncate'>{file.name}</span>
            <button
              type='button'
              onClick={() => {
                setFile(null);
                setPreview(null);
                setSizeError(null);
              }}
              aria-label='Delete image'
              className='absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full w-7 h-7 flex items-center justify-center shadow transition'
            >
              <MdDelete size={20} />
            </button>
          </div>
        )
      ) : (
        <label
          htmlFor={dropzoneId || 'dropzone-file'}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className={`flex flex-col items-center justify-center w-full ${
            compact ? 'h-36' : 'min-w-[400px] h-64'
          } border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-white`}
        >
          <div className='flex flex-col items-center justify-center pt-5 pb-6'>
            <svg
              className={`${
                compact ? 'w-6 h-6 mb-2' : 'w-8 h-8 mb-4'
              } text-gray-700`}
              aria-hidden='true'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 20 16'
            >
              <path
                stroke='currentColor'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2'
              />
            </svg>
            <p className='mb-2 text-sm text-gray-700'>
              <span className='font-semibold'>Click to upload</span> or drag and
              drop
            </p>
            {!compact && (
              <p className='text-xs text-gray-700'>
                {fileTypesTagline ?? 'SVG, PNG or JPG'}
              </p>
            )}
          </div>
          <input
            ref={inputRef}
            id={dropzoneId || 'dropzone-file'}
            type='file'
            className='hidden'
            onChange={handleFileChange}
          />
        </label>
      )}
      {sizeError && <p className='text-sm text-red-600'>{sizeError}</p>}
      {enableImageEditing && (
        <ImageEditorModal
          isOpen={!!fileToEdit}
          file={fileToEdit}
          title={
            editorTitle ??
            (label ? `Edit ${label.toLowerCase()}` : 'Edit image')
          }
          lockedAspect={imageCropAspect}
          onCancel={() => {
            setFileToEdit(null);
            clearFileInput();
          }}
          onApply={(editedFile) => {
            stageFile(editedFile);
            setFileToEdit(null);
            clearFileInput();
          }}
        />
      )}
    </div>
  );
}
